import {
  cleanInputCreate,
  cleanInputUpdate,
  type CreateReconciliationSettingInput,
  type CreateReconciliationSettingMutationVariables,
  createReconciliationSetting as createMutation,
  deleteReconciliationSetting as deleteMutation,
  type DeleteReconciliationSettingMutationVariables,
  getLeaseAllocationModeOverride,
  getOwnerAllocationModeOverride,
  getReadId,
  getReconciliationSetting as getQuery,
  getTableClientId,
  RECONCILIATION_SETTING_TABLE_NAME,
  ReconciliationAllocationMode,
  type ReconciliationSetting,
  ReconciliationSettingType,
  syncReconciliationSettings,
  updateReconciliationSetting as updateMutationQuery,
  type UpdateReconciliationSettingInput,
} from '@rentguru/commons-utils';
import { deleteEntityWithFetchBefore, graphqlClient, mutation, toGraphQLApiError } from '@up2rent/graphql';
import isNil from 'lodash/isNil';
import { useEffect, useRef, useState } from 'react';

import { useUser } from './UserContext';

export interface ReconciliationSettingsContext {
  reconciliationSettingsLoading: boolean;
  reconciliationSettings: ReconciliationSetting[];
  createReconciliationSetting: (
    input: Omit<CreateReconciliationSettingInput, 'clientId' | 'readId'>
  ) => Promise<ReconciliationSetting>;
  updateReconciliationSetting: (updates: UpdateReconciliationSettingInput) => Promise<ReconciliationSetting>;
  deleteReconciliationSetting: (reconciliationSetting: ReconciliationSetting) => Promise<ReconciliationSetting>;
  reconciliationSettingsError: boolean;
  forceDetailedAllocationModeForOwners: (ownerIds: string[], leaseIds?: string[]) => Promise<void>;
}

interface SyncReconciliationSettingsPage {
  syncReconciliationSettings: { items: ReconciliationSetting[]; nextToken?: string | null };
}

// `list` turns a failed read into an empty result; enforcing on that would duplicate existing overrides
const fetchReconciliationSettingsOrThrow = async (clientId: string): Promise<ReconciliationSetting[]> => {
  const settings: ReconciliationSetting[] = [];
  let nextToken: string | null = null;
  try {
    do {
      const page: SyncReconciliationSettingsPage = await graphqlClient.request<SyncReconciliationSettingsPage>(
        syncReconciliationSettings,
        {
          filter: { clientId: { eq: getTableClientId(clientId, RECONCILIATION_SETTING_TABLE_NAME) } },
          limit: 1000,
          nextToken,
        }
      );
      settings.push(...page.syncReconciliationSettings.items.filter((setting) => setting._deleted !== true));
      nextToken = page.syncReconciliationSettings.nextToken ?? null;
    } while (!isNil(nextToken));
  } catch (error) {
    throw toGraphQLApiError(error, 'syncReconciliationSettings');
  }
  return settings;
};

export const useReconciliationSettings = (shouldFetch: boolean = false): ReconciliationSettingsContext => {
  const [reconciliationSettingsLoading, setReconciliationSettingsLoading] = useState<boolean>(shouldFetch);
  const [reconciliationSettings, setReconciliationSettings] = useState<ReconciliationSetting[]>([]);
  const [reconciliationSettingsError, setReconciliationSettingsError] = useState<boolean>(false);
  const latestSettings = useRef<ReconciliationSetting[]>([]);
  const fetchedClientId = useRef<string | null>(null);
  const enforcementQueue = useRef<Promise<void>>(Promise.resolve());
  const { clientId } = useUser();

  const replaceSettings = (update: (previous: ReconciliationSetting[]) => ReconciliationSetting[]) => {
    latestSettings.current = update(latestSettings.current);
    setReconciliationSettings(latestSettings.current);
  };

  useEffect(() => {
    if (!shouldFetch || isNil(clientId) || fetchedClientId.current === clientId) {
      return undefined;
    }
    let isCurrentClient = true;
    fetchedClientId.current = clientId;
    replaceSettings(() => []);
    setReconciliationSettingsLoading(true);
    setReconciliationSettingsError(false);
    fetchReconciliationSettingsOrThrow(clientId)
      .then((result) => {
        if (isCurrentClient) {
          replaceSettings(() => result);
        }
      })
      .catch(() => {
        if (isCurrentClient) {
          setReconciliationSettingsError(true);
        }
      })
      .finally(() => {
        if (isCurrentClient) {
          setReconciliationSettingsLoading(false);
        }
      });
    return () => {
      isCurrentClient = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFetch, clientId]);

  const createReconciliationSetting = async (
    input: Omit<CreateReconciliationSettingInput, 'clientId' | 'readId'>
  ): Promise<ReconciliationSetting> => {
    const created = await mutation<ReconciliationSetting, CreateReconciliationSettingMutationVariables>(
      createMutation,
      {
        input: {
          ...(cleanInputCreate(input) as CreateReconciliationSettingInput),
          readId: getReadId(clientId!, RECONCILIATION_SETTING_TABLE_NAME),
          clientId: getTableClientId(clientId!, RECONCILIATION_SETTING_TABLE_NAME),
        },
      }
    );
    replaceSettings((previous) => [...previous, created]);
    return created;
  };

  const updateReconciliationSetting = async (
    updates: UpdateReconciliationSettingInput
  ): Promise<ReconciliationSetting> => {
    const updated = await mutation<ReconciliationSetting>(updateMutationQuery, {
      input: cleanInputUpdate(updates, false),
    });
    replaceSettings((previous) => previous.map((setting) => (setting.id === updated.id ? updated : setting)));
    return updated;
  };

  const deleteReconciliationSetting = async (
    reconciliationSetting: ReconciliationSetting
  ): Promise<ReconciliationSetting> => {
    const deleted = await deleteEntityWithFetchBefore<
      ReconciliationSetting,
      DeleteReconciliationSettingMutationVariables
    >(reconciliationSetting, getQuery, deleteMutation);
    replaceSettings((previous) => previous.filter((setting) => setting.id !== reconciliationSetting.id));
    return deleted;
  };

  const applyDetailedAllocationModeForOwners = async (ownerIds: string[], leaseIds: string[]): Promise<void> => {
    // The settings screen edits overrides through another instance of this hook
    const currentSettings = await fetchReconciliationSettingsOrThrow(clientId!);
    fetchedClientId.current = clientId ?? null;
    replaceSettings(() => currentSettings);
    for (const ownerId of ownerIds) {
      const existingOverride = getOwnerAllocationModeOverride(ownerId, latestSettings.current);
      if (isNil(existingOverride)) {
        await createReconciliationSetting({
          type: ReconciliationSettingType.OWNER,
          ownerId,
          allocationMode: ReconciliationAllocationMode.DETAILED,
        });
      } else if (existingOverride.allocationMode !== ReconciliationAllocationMode.DETAILED) {
        await updateReconciliationSetting({
          id: existingOverride.id,
          _version: existingOverride._version,
          allocationMode: ReconciliationAllocationMode.DETAILED,
        });
      }
    }
    for (const leaseId of leaseIds) {
      const leaseOverride = getLeaseAllocationModeOverride(leaseId, latestSettings.current);
      if (!isNil(leaseOverride) && leaseOverride.allocationMode !== ReconciliationAllocationMode.DETAILED) {
        await updateReconciliationSetting({
          id: leaseOverride.id,
          _version: leaseOverride._version,
          allocationMode: ReconciliationAllocationMode.DETAILED,
        });
      }
    }
  };

  // Serialized so concurrent callers never both create an override for the same owner
  const forceDetailedAllocationModeForOwners = (ownerIds: string[], leaseIds: string[] = []): Promise<void> => {
    const run = enforcementQueue.current.then(() => applyDetailedAllocationModeForOwners(ownerIds, leaseIds));
    enforcementQueue.current = run.catch(() => undefined);
    return run;
  };

  return {
    reconciliationSettingsLoading,
    reconciliationSettingsError,
    reconciliationSettings,
    createReconciliationSetting,
    updateReconciliationSetting,
    deleteReconciliationSetting,
    forceDetailedAllocationModeForOwners,
  };
};
